/**
 * Stepper dos passos 1 a 5.
 *
 * Réplica do Figma (`web-step1-titular` … `web-step5-aceite`): cinco círculos
 * numerados ligados por uma linha contínua, ocupando toda a largura. Passo
 * concluído vira check, o corrente fica na cor da marca com o número, os futuros
 * ficam cinza. O design não usa rótulos de texto — a régua é só posicional, e o
 * título de cada tela já diz onde o titular está.
 *
 * Fica oculto no Step 0 (o frame `web-login` não tem stepper), por isso o
 * componente devolve `null` em vez de o pai condicionar a renderização.
 */

import CheckIcon from "@mui/icons-material/Check";
import styled from "styled-components";

import { COLORS } from "../tokens";
import { ExtensionStep } from "../types";

/** Passos exibidos na régua: do 1 (Titular) ao 5 (Confirmar e Enviar). */
const STEP_COUNT = 5;

const CIRCLE_SIZE = "24px";

const Track = styled.ol`
  width: 100%;
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

/**
 * Cada passo ocupa uma fração igual da régua. O primeiro não cresce, para que a
 * linha só apareça ENTRE círculos e não sobre a borda do container.
 */
const StepItem = styled.li<{ $isFirst: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex: ${({ $isFirst }) => ($isFirst ? "0 0 auto" : "1 1 0")};
  min-width: 0;
`;

const Connector = styled.span`
  flex: 1 1 auto;
  height: 1px;
  min-width: 8px;
  background-color: ${COLORS.brand};
`;

const Circle = styled.span<{ $active: boolean }>`
  flex: 0 0 auto;
  width: ${CIRCLE_SIZE};
  height: ${CIRCLE_SIZE};
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  background-color: ${({ $active }) =>
    $active ? COLORS.brand : COLORS.borderStrong};
  color: ${COLORS.surface};

  /* O tema global pinta os SVGs com a cor de texto padrão, que sobrepõe a cor
     herdada do círculo e deixa o check escuro sobre o roxo. Fixar cor e
     preenchimento aqui garante o contraste. */
  & > svg {
    font-size: 16px;
    color: ${COLORS.surface};
    fill: currentColor;
  }
`;

interface IProps {
  currentStep: ExtensionStep;
}

export function ExtensionStepper({ currentStep }: IProps) {
  if (currentStep === ExtensionStep.Login) return null;

  /** `ExtensionStep.Holder` é 1, e a régua também começa em 1. */
  const currentNumber = currentStep;

  return (
    <Track aria-label={`Passo ${currentNumber} de ${STEP_COUNT}`}>
      {Array.from({ length: STEP_COUNT }, (_, index) => {
        const stepNumber = index + 1;
        const isDone = stepNumber < currentNumber;
        const isCurrent = stepNumber === currentNumber;

        return (
          <StepItem
            key={stepNumber}
            $isFirst={index === 0}
            aria-current={isCurrent ? "step" : undefined}
          >
            {index > 0 && <Connector />}
            <Circle $active={isDone || isCurrent}>
              {isDone ? <CheckIcon /> : stepNumber}
            </Circle>
          </StepItem>
        );
      })}
    </Track>
  );
}
